import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api-fetch";
import { formatApiError } from "@/lib/api-error";
import type { UserWithProfile } from "@/types/user-profile";

export const DEFAULT_EVALUATION_WEIGHT = 5;

/** Writes to another user's profile go through the API (migration 133 scoped /rest/v1 updates to the caller's own row). */
export function useSaveEvaluationWeight(user: UserWithProfile) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const save = async (weight: number) => {
    setIsSaving(true);
    try {
      await apiFetch(`${SUPABASE_URL}admin/usuarios/${user.id}/peso-avaliacao`, {
        method: "PUT",
        body: JSON.stringify({ evaluation_weight: weight }),
      });
      toast({
        title: "Peso atualizado",
        description: `O peso de avaliação de ${user.full_name || user.username} foi atualizado para ${weight}.`,
      });
      queryClient.invalidateQueries({ queryKey: ["users_with_profiles"] });
      return true;
    } catch (error: unknown) {
      const { detail } = formatApiError(error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o peso. " + detail,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return { save, isSaving };
}
